import { MONITORING_CONFIG } from '@/config/performance';

export const WIZARD_ANALYTICS_EVENTS = {
  DRAFT_CREATED: 'wizard.draft_created',
  STEP_VIEWED: 'wizard.step_viewed',
  STEP_COMPLETED: 'wizard.step_completed',
  DRAFT_ABANDONED: 'wizard.draft_abandoned',
  PUBLISH_ATTEMPT: 'wizard.publish_attempt',
  PUBLISH_SUCCESS: 'wizard.publish_success',
  PUBLISH_FAILED: 'wizard.publish_failed',
  SAVE_FAILED: 'wizard.save_failed',
} as const;

type WizardAnalyticsPayload = Record<string, string | number | boolean | string[] | null | undefined>;

const STEP_TIMERS_KEY = 'wizard_step_timers';

function analyticsEnabled(): boolean {
  return MONITORING_CONFIG.ANALYTICS.ENABLED && MONITORING_CONFIG.ANALYTICS.TRACK_EVENTS;
}

function shouldSample(): boolean {
  if (typeof window === 'undefined') return false;
  if (MONITORING_CONFIG.ANALYTICS.SAMPLE_RATE >= 1) return true;
  return Math.random() < MONITORING_CONFIG.ANALYTICS.SAMPLE_RATE;
}

export function trackWizardEvent(
  event: (typeof WIZARD_ANALYTICS_EVENTS)[keyof typeof WIZARD_ANALYTICS_EVENTS],
  payload: WizardAnalyticsPayload = {},
): void {
  if (!analyticsEnabled() || !shouldSample()) return;

  const detail = {
    event,
    timestamp: Date.now(),
    ...payload,
  };

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('enthios:wizard-analytics', { detail }));
  }

  if (process.env.NODE_ENV === 'development') {
     
    console.debug('[wizard-analytics]', detail);
  }
}

export function startWizardStepTimer(eventId: string, stepId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(STEP_TIMERS_KEY);
    const timers = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    timers[`${eventId}:${stepId}`] = Date.now();
    sessionStorage.setItem(STEP_TIMERS_KEY, JSON.stringify(timers));
  } catch {
    /* ignore storage errors */
  }
}

export function finishWizardStepTimer(eventId: string, stepId: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const key = `${eventId}:${stepId}`;
    const raw = sessionStorage.getItem(STEP_TIMERS_KEY);
    const timers = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const started = timers[key];
    if (!started) return null;
    delete timers[key];
    sessionStorage.setItem(STEP_TIMERS_KEY, JSON.stringify(timers));
    return Date.now() - started;
  } catch {
    return null;
  }
}

export function trackWizardStepViewed(eventId: string, stepIndex: number, stepId: string): void {
  startWizardStepTimer(eventId, stepId);
  trackWizardEvent(WIZARD_ANALYTICS_EVENTS.STEP_VIEWED, {
    event_id: eventId,
    step_index: stepIndex,
    step_id: stepId,
  });
}

export function trackWizardStepCompleted(
  eventId: string,
  stepIndex: number,
  stepId: string,
): void {
  const durationMs = finishWizardStepTimer(eventId, stepId);
  trackWizardEvent(WIZARD_ANALYTICS_EVENTS.STEP_COMPLETED, {
    event_id: eventId,
    step_index: stepIndex,
    step_id: stepId,
    duration_ms: durationMs ?? undefined,
  });
}

export function trackWizardDraftAbandoned(eventId: string, lastStepIndex: number, lastStepId: string): void {
  trackWizardEvent(WIZARD_ANALYTICS_EVENTS.DRAFT_ABANDONED, {
    event_id: eventId,
    last_step_index: lastStepIndex,
    last_step_id: lastStepId,
  });
}
